import { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2, Plus, Edit, Trash2, Languages } from 'lucide-react';
import { AdminPageShell } from '../components/AdminShared';

export default function AdminLanguagesPage() {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editLang, setEditLang] = useState(null);
  const [form, setForm] = useState({ code: '', name: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const res = await adminAPI.listLanguages(); setLanguages(res.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreate = () => { setEditLang(null); setForm({ code: '', name: '' }); setShowDialog(true); };
  const openEdit = (l) => { setEditLang(l); setForm({ code: l.code, name: l.name }); setShowDialog(true); };

  const handleSubmit = async () => {
    try {
      if (editLang) await adminAPI.updateLanguage(editLang.id, form);
      else await adminAPI.createLanguage(form);
      setShowDialog(false); load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this language?')) return;
    try { await adminAPI.deleteLanguage(id); load(); } catch (e) { console.error(e); }
  };

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell>
      <div className="p-6 md:p-8 max-w-4xl animate-fade-in" data-testid="admin-languages-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Languages</h1>
            <p className="text-muted-foreground mt-1 text-sm">Manage supported languages and assign them to fee schedule tiers</p>
          </div>
          <Button onClick={openCreate} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="add-language-button">
            <Plus className="w-4 h-4 mr-2" />Add Language
          </Button>
        </div>

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {languages.map(l => (
                <TableRow key={l.id} data-testid={`lang-row-${l.id}`}>
                  <TableCell><Badge variant="outline" className="font-mono text-xs">{l.code}</Badge></TableCell>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(l)} data-testid={`edit-lang-${l.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(l.id)} className="text-destructive" data-testid={`delete-lang-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {languages.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No languages configured</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="language-dialog">
            <DialogHeader><DialogTitle>{editLang ? 'Edit Language' : 'Add Language'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Language Code *</Label><Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. de" disabled={!!editLang} data-testid="lang-code-input" /></div>
              <div className="space-y-2"><Label>Language Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. German" data-testid="lang-name-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!form.code || !form.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-language">{editLang ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminPageShell>
  );
}
